import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { ChevronDown, HelpCircle, Mail, Phone } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    document.title = 'FAQ | Keto Vybe';
  }, []);

  const faqs = [
    {
      category: 'Orders & Shipping',
      questions: [
        {
          q: 'How long does shipping take?',
          a: 'Orders are processed within 1-2 business days. Standard shipping via USPS takes 3-5 business days once shipped. You\'ll receive tracking information via email.'
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Yes! We offer free standard shipping on all orders over $99 within the continental United States.'
        },
        {
          q: 'Can I track my order?',
          a: 'Absolutely. Once your order ships, you\'ll receive an email with tracking information so you can follow your package every step of the way.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Currently, we only ship within the United States. We hope to expand to international shipping in the future.'
        }
      ]
    },
    {
      category: 'Products',
      questions: [
        {
          q: 'Are your supplements keto-friendly?',
          a: 'Yes! All our products are specifically formulated for the ketogenic lifestyle. They contain zero or minimal carbs and are designed to support ketosis.'
        },
        {
          q: 'Are your products third-party tested?',
          a: 'Yes, all our supplements undergo rigorous third-party testing for purity, potency, and safety. We believe in complete transparency.'
        },
        {
          q: 'What if a product doesn\'t work for me?',
          a: 'We offer a 30-day return policy on unopened products. If you\'re not satisfied, contact us and we\'ll help you with a return or exchange.'
        },
        {
          q: 'How should I store the supplements?',
          a: 'Store in a cool, dry place away from direct sunlight. Keep the lid tightly closed. Some products may require refrigeration after opening - check the label.'
        }
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We offer a 30-day return policy on unopened, sealed products in their original packaging. Contact us to initiate a return.'
        },
        {
          q: 'How do I return a product?',
          a: 'Email us at ' + CONTACT_INFO.email + ' with your order number. We\'ll provide return instructions and the shipping address.'
        },
        {
          q: 'How long do refunds take?',
          a: 'Once we receive your return, refunds are processed within 5-7 business days. It may take additional time for your bank to reflect the credit.'
        }
      ]
    },
    {
      category: 'Account & Payment',
      questions: [
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit cards including Visa, Mastercard, American Express, and Discover. All transactions are securely processed.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes, we use industry-standard SSL encryption to protect your payment information. We never store your full credit card details on our servers.'
        },
        {
          q: 'What will appear on my credit card statement?',
          a: 'Charges will appear as "KETO VYBE" on your credit card statement.'
        }
      ]
    }
  ];

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  let globalIndex = 0;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-64 lg:h-72 overflow-hidden">
          <img 
            src={siteImages.aboutBanner}
            alt="FAQ"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Help Center</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                Frequently Asked Questions
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Find answers to common questions
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {faqs.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-10">
              <h2 className="font-display text-xl text-white mb-5 flex items-center gap-3">
                <div className="w-8 h-8 bg-vybe-600/20 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-4 h-4 text-vybe-400" />
                </div>
                {section.category}
              </h2>
              <div className="space-y-3">
                {section.questions.map((faq, i) => {
                  const currentIndex = globalIndex++;
                  return (
                    <div 
                      key={i} 
                      className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden"
                    >
                      <button
                        onClick={() => toggleFaq(currentIndex)}
                        className="w-full flex items-center justify-between p-5 text-left"
                      >
                        <span className="font-medium text-white pr-4">{faq.q}</span>
                        <ChevronDown className={`w-5 h-5 text-vybe-400 flex-shrink-0 transition-transform ${openIndex === currentIndex ? 'rotate-180' : ''}`} />
                      </button>
                      {openIndex === currentIndex && (
                        <div className="px-5 pb-5 text-slate-400 text-sm leading-relaxed border-t border-slate-700/50 pt-4">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Contact CTA */}
          <div className="mt-12 bg-gradient-to-br from-vybe-600/20 to-slate-800/50 rounded-2xl p-8 border border-vybe-500/30 text-center">
            <h3 className="font-display text-xl text-white mb-3">Still Have Questions?</h3>
            <p className="text-slate-400 mb-6">Our support team is here to help you.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-vybe-600 text-white font-semibold rounded-xl hover:bg-vybe-500 transition-all"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 text-white font-semibold rounded-xl hover:bg-slate-600 transition-all"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQPage;
